
import { useState, useEffect, useRef } from 'react';

export default function AtelierUseCases() {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const cases = [
    {
      image:
        'https://public.readdy.ai/ai/img_res/77711459f066ef9d61c248f79a35bd90.jpg',
      title: 'イラスト・デザイン制作',
      desc: '最適な角度で長時間の制作作業も快適に。Procreateやクリスタでの作業効率が格段にアップ。',
      icon: 'ri-brush-line',
    },
    {
      image:
        'https://readdy.ai/api/search-image?query=person%20taking%20handwritten%20notes%20on%20iPad%20with%20stylus%20in%20coffee%20shop%20modern%20interior%20natural%20lighting%20warm%20tones%20minimalist%20aesthetic%20professional%20photography%20dark%20background&width=600&height=400&seq=af-usecase-002&orientation=landscape',
      title: 'ノートテイキング',
      desc: '紙のノートのような自然な書き心地。会議やセミナーでのメモ取りに最適なポジションを実現。',
      icon: 'ri-edit-line',
    },
    {
      image:
        'https://readdy.ai/api/search-image?query=person%20watching%20movie%20on%20iPad%20tablet%20stand%20on%20wooden%20desk%20cozy%20evening%20setting%20warm%20ambient%20lighting%20modern%20minimalist%20room%20dark%20aesthetic%20professional%20photography&width=600&height=400&seq=af-usecase-003&orientation=landscape',
      title: 'エンターテインメント',
      desc: 'スタンドモードで動画視聴やビデオ通話も快適。ハンズフリーで楽しめる最適な視聴角度。',
      icon: 'ri-play-circle-line',
    },
  ];

  return (
    <section
      ref={ref}
      className="py-20 sm:py-28 bg-[#0F0F0F] relative"
      aria-labelledby="af-usecases-heading"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <div
          className={`text-center mb-12 sm:mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2
            id="af-usecases-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight"
          >
            あらゆるシーンで活躍
          </h2>
          <p className="text-sm sm:text-base text-white/40 font-light">
            一つのフォリオが、あなたのiPad体験を変える
          </p>
        </div>

        {/* Use‑case cards */}
        <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
          {cases.map((c, i) => (
            <article
              key={c.title}
              className={`
                group rounded-xl overflow-hidden
                bg-white/5                 /* replaced invalid bg-white/[0.03] */
                border border-white/6      /* replaced invalid border-white/[0.06] */
                hover:border-white/10
                transition-all duration-500
                ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
              `}
              style={{ transitionDelay: `${i * 150 + 200}ms` }}
            >
              <div className="relative w-full h-52 sm:h-56 overflow-hidden">
                <img
                  src={c.image}
                  alt={c.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                  width={600}
                  height={400}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/60 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-white/10 backdrop-blur-md rounded-lg border border-white/15">
                    <i className={`${c.icon} text-[#8E7AB5] text-lg`} aria-hidden="true" />
                  </div>
                </div>
              </div>

              <div className="p-6 sm:p-7">
                <h3 className="text-white font-medium text-base sm:text-lg mb-2">{c.title}</h3>
                <p className="text-white/35 text-xs sm:text-sm font-light leading-relaxed">{c.desc}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
